import React, { Suspense, lazy } from 'react';
import { Navigate, Route, Routes } from 'react-router-dom';

import store from '../store';

import { initStickyOffset } from '../utils';

import { HIDE_CART_MODAL, HIDE_QUICKVIEW } from '../constants/action-types';

import PrivateClient from './client/private-client-route';
import PrivateSeller from './seller/private-seller-route';
import PublicRoute from './public-route';

// Critical routes stay eager so homepage / catalog / PDP start without extra round-trips.
import FullWidth from "../components/pages/categories/shop-full-width";
import DefaultProduct from "../components/pages/products/default-product";
import HomePage from "../components/home";
import ErrorPage from "../components/pages/others/404/page-error";
import Layout from '../components/layout';

const Login = lazy(() => import("../components/pages/others/login/login"));
const DashboardClient = lazy(() => import("../components/pages/others/dashboard/dashboard-client"));
const DashboardSeller = lazy(() => import("../components/pages/others/dashboard/dashboard-seller"));
const SearchPage = lazy(() => import("../components/pages/search/search"));
const ShoppingCart = lazy(() => import("../components/pages/others/cart/shopping-cart"));
const DeliveryAndPayment = lazy(() => import("../components/pages/others/cart/delivery-and-payment"));
const Review = lazy(() => import("../components/pages/others/cart/checkout-review"));
const Payment = lazy(() => import("../components/pages/others/cart/payment"));
const Password = lazy(() => import("../components/pages/others/repeat-password"));
const RegisterClient = lazy(() => import("../components/pages/others/register-client"));
const ConfirmRegister = lazy(() => import("../components/pages/others/register-confirm/confirm"));
const Blog = lazy(() => import("../components/pages/others/blog/blog"));
const BlogPost = lazy(() => import("../components/pages/others/blog/single"));
const Cms = lazy(() => import("../components/pages/others/info/cms"));
const Contact = lazy(() => import("../components/pages/others/info/contact"));
const TechnicalSupport = lazy(() => import("../components/pages/others/support/technical-support"));
const CostAndMethodDelivery = lazy(() => import("../components/pages/others/support/cost-and-method-delivery"));
const ReturnAndComplaints = lazy(() => import("../components/pages/others/support/returns-and-complaints"));
const PrivacyPolicy = lazy(() => import("../components/pages/others/info/privacy-policy"));
const AboutCompany = lazy(() => import("../components/pages/others/about-company"));
const BuildingMaterials = lazy(() => import("../components/pages/others/about-company/offers/BuildingMaterials"));
const TransportHds = lazy(() => import("../components/pages/others/about-company/offers/TransportHds"));
const KeyDuplicate = lazy(() => import("../components/pages/others/about-company/offers/KeyDuplicate"));
const LawnMowerKnives = lazy(() => import("../components/pages/others/about-company/offers/LawnMowerKnives"));
const HouseMaterials = lazy(() => import("../components/pages/others/about-company/offers/landing-page/HouseMaterials"));
const ArchedEdges = lazy(() => import("../components/pages/others/about-company/offers/landing-page/ArchedEdges"));
const PartnerProgram = lazy(() => import("../components/pages/others/about-company/terms/PartnerProgram"));
const Voucher = lazy(() => import("../components/pages/others/about-company/terms/Voucher"));
const ShopRules = lazy(() => import("../components/pages/others/about-company/terms/ShopRules"));
const Wishlist = lazy(() => import("../components/pages/others/wishlist/wishilist"));

function RouteFallback() {
  return (
    <div className="container py-5" style={{ minHeight: '60vh' }} aria-busy="true">
      <div className="product-card-skeleton__line product-card-skeleton__line--title" style={{ width: '40%', marginBottom: '1rem' }} />
      <div className="product-card-skeleton__line" style={{ width: '70%' }} />
      <div className="product-card-skeleton__line" style={{ width: '55%' }} />
    </div>
  );
}

export default function Router() {
  store.dispatch({ type: HIDE_CART_MODAL });
  store.dispatch({ type: HIDE_QUICKVIEW });
  initStickyOffset();

  return (
    <Suspense fallback={<RouteFallback />}>
      <Layout>
        <Routes>
          <Route path="produkty/:category_slug?" element={<FullWidth />} />
          <Route path="produkty/:category_slug/:product_slug" element={<DefaultProduct />} />

          <Route path="logowanie" element={<PublicRoute><Login /></PublicRoute>} />

          <Route path='klient/:board/:order_id?' element={<PrivateClient><DashboardClient /></PrivateClient>} />

          <Route path='sprzedawca/:board/:order_id?' element={<PrivateSeller><DashboardSeller /></PrivateSeller>} />

          <Route exact path={`${process.env.PUBLIC_URL}/`} element={<HomePage />} />
          <Route exact path={`${process.env.PUBLIC_URL}/szukaj/:searchInput`} element={<SearchPage />} />

          <Route exact path={`${process.env.PUBLIC_URL}/koszyk`} element={<ShoppingCart />} />
          <Route exact path={`${process.env.PUBLIC_URL}/koszyk/dostawa-i-platnosc`} element={<DeliveryAndPayment />} />
          <Route exact path={`${process.env.PUBLIC_URL}/koszyk/dostawa-i-platnosc/gotowe`} element={<Review />} />
          <Route exact path={`${process.env.PUBLIC_URL}/platnosc-w-trakcie-przetwarzania`} element={<Payment />} />

          <Route exact path={`${process.env.PUBLIC_URL}/zresetuj-haslo`} element={<Password />} />
          <Route exact path={`${process.env.PUBLIC_URL}/rejestracja`} element={<RegisterClient />} />
          <Route exact path={`${process.env.PUBLIC_URL}/potwierdzenie-rejestracji/:token`} element={<ConfirmRegister />} />

          <Route exact path={`${process.env.PUBLIC_URL}/serwis-informacyjny/:category_slug?`} element={<Blog />} />
          <Route exact path={`${process.env.PUBLIC_URL}/serwis-informacyjny/:category_slug/:post_slug`} element={<BlogPost />} />

          <Route exact path={`${process.env.PUBLIC_URL}/info/:cms_slug`} element={<Cms />} />
          <Route exact path={`${process.env.PUBLIC_URL}/pomoc/:cms_slug`} element={<Cms />} />
          <Route exact path={`${process.env.PUBLIC_URL}/kontakt`} element={<Contact />} />
          <Route exact path={`${process.env.PUBLIC_URL}/pomoc-techniczna`} element={<TechnicalSupport />} />
          <Route exact path={`${process.env.PUBLIC_URL}/koszty-i-sposob-dostawy`} element={<CostAndMethodDelivery />} />
          <Route exact path={`${process.env.PUBLIC_URL}/zwroty-i-reklamacje`} element={<ReturnAndComplaints />} />
          <Route exact path={`${process.env.PUBLIC_URL}/polityka-prywatnosci`} element={<PrivacyPolicy />} />
          <Route exact path={`${process.env.PUBLIC_URL}/o-firmie`} element={<AboutCompany />} />
          <Route exact path={`${process.env.PUBLIC_URL}/oferta/materialy-budowlane-i-narzedzia`} element={<BuildingMaterials />} />
          <Route exact path={`${process.env.PUBLIC_URL}/oferta/sprzedaz-i-serwis-narzedzi-ogrodowych`} element={<Navigate to={`${process.env.PUBLIC_URL}/`} replace />} />
          <Route exact path={`${process.env.PUBLIC_URL}/oferta/uslugi-transportowe-z-hds`} element={<TransportHds />} />
          <Route exact path={`${process.env.PUBLIC_URL}/oferta/dorabianie-kluczy`} element={<KeyDuplicate />} />
          <Route exact path={`${process.env.PUBLIC_URL}/oferta/noze-do-kosiarek`} element={<LawnMowerKnives />} />
          <Route exact path={`${process.env.PUBLIC_URL}/oferta/z-czego-budowac-dom`} element={<HouseMaterials />} />
          <Route exact path={`${process.env.PUBLIC_URL}/oferta/:slug`} element={<ArchedEdges />} />
          <Route exact path={`${process.env.PUBLIC_URL}/regulamin-programu-partnerskiego-sklepu-stacjonarnego`} element={<PartnerProgram />} />
          <Route exact path={`${process.env.PUBLIC_URL}/regulamin-bonu-podarunkowego`} element={<Voucher />} />
          <Route exact path={`${process.env.PUBLIC_URL}/regulamin`} element={<ShopRules />} />

          <Route exact path={`${process.env.PUBLIC_URL}/info/o-ciasteczkach`} element={<ErrorPage />} />

          <Route exact path={`${process.env.PUBLIC_URL}/ulubione`} element={<Wishlist />} />
          <Route exact path={`${process.env.PUBLIC_URL}/404`} element={<ErrorPage />} />

          <Route path="*" element={<ErrorPage />} />
        </Routes>
      </Layout>
    </Suspense>
  );
}
